import React, { useState, useEffect, useRef } from 'react'
import api from '../api'
import Button from '../components/Button'
import WalkAndTalk from '../components/WalkAndTalk'

interface Entry {
  id: string
  parent_entry_id: string | null
  content: string
  privacy_level: string
  location_context: string | null
  mood_score: number | null
  tags: string[]
  entry_date: string
  input_method: string
  created_at: string
  ai_insight?: {
    sentiment_score: number | null
    summary: string | null
    coach_prompt: string | null
  }
}

interface CrisisSupport {
  message: string
  is_crisis: boolean
}

type RecordingState = 'idle' | 'recording' | 'transcribing' | 'done'
type LocationContext = 'home' | 'work' | 'commuting' | 'outside' | 'gym' | 'other' | null

const LOCATIONS = [
  {
    value: 'home' as LocationContext,
    icon: '🏠',
    label: 'Home',
    prompt: 'Have you properly switched off from work today? What does your energy feel like right now?',
  },
  {
    value: 'work' as LocationContext,
    icon: '💼',
    label: 'Work',
    prompt: 'How is the pressure feeling today? Is there anything weighing on you that you haven\'t had a chance to sit with?',
  },
  {
    value: 'commuting' as LocationContext,
    icon: '🚗',
    label: 'Commuting',
    prompt: 'What\'s running through your mind right now? Use this time to empty your thoughts before you arrive.',
  },
  {
    value: 'outside' as LocationContext,
    icon: '🌳',
    label: 'Outside',
    prompt: 'What got you outside today? Notice how your body feels when you\'re moving and in open space.',
  },
  {
    value: 'gym' as LocationContext,
    icon: '🏃',
    label: 'Gym',
    prompt: 'How did that feel? Physical movement is one of the most powerful burnout antidotes — notice how you feel after.',
  },
  {
    value: 'other' as LocationContext,
    icon: '📍',
    label: 'Other',
    prompt: 'What\'s on your mind right now? Speak freely — there are no wrong answers here.',
  },
]

function PrivacyBadge({
  entryId,
  privacyLevel,
  onUpdate,
}: {
  entryId: string
  privacyLevel: string
  onUpdate: (level: string) => void
}) {
  const [updating, setUpdating] = useState(false)
  const [showMenu, setShowMenu] = useState(false)

  const levels = [
    { value: 'private', label: 'Private', icon: '🔒', color: '#78716C' },
    { value: 'legacy', label: 'Legacy', icon: '🕊️', color: '#C8963E' },
    { value: 'archive', label: 'Archive', icon: '📦', color: '#2D6A4F' },
  ]

  const current = levels.find(l => l.value === privacyLevel) || levels[0]

  const handleChange = async (newLevel: string) => {
    setUpdating(true)
    setShowMenu(false)
    try {
      await api.patch('/entries/' + entryId + '/privacy', {
        privacy_level: newLevel
      })
      onUpdate(newLevel)
    } catch (err) {
      console.error('Failed to update privacy', err)
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={updating}
        style={{
          display: 'flex', alignItems: 'center', gap: '4px',
          padding: '2px 8px', fontSize: '11px',
          color: current.color,
          background: current.color + '15',
          border: '1px solid ' + current.color + '40',
          borderRadius: '9999px',
          cursor: 'pointer', fontFamily: 'var(--font-body)',
          opacity: updating ? 0.6 : 1,
        }}
      >
        {current.icon} {current.label}
      </button>

      {showMenu && (
        <div style={{
          position: 'absolute', right: 0, top: '28px',
          background: 'var(--white)',
          border: '1px solid var(--cream-dark)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-md)',
          zIndex: 100, overflow: 'hidden',
          minWidth: '140px',
        }}>
          {levels.map(level => (
            <button
              key={level.value}
              onClick={() => handleChange(level.value)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                width: '100%', padding: '10px 14px',
                fontSize: '13px', color: level.color,
                background: privacyLevel === level.value
                  ? level.color + '10'
                  : 'transparent',
                border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                fontWeight: privacyLevel === level.value ? 600 : 400,
                textAlign: 'left' as const,
              }}
            >
              {level.icon} {level.label}
              {privacyLevel === level.value && (
                <span style={{ marginLeft: 'auto', fontSize: '10px' }}>✓</span>
              )}
            </button>
          ))}
          <div style={{
            padding: '8px 14px',
            borderTop: '1px solid var(--cream-dark)',
            fontSize: '10px', color: 'var(--muted)', lineHeight: 1.5,
          }}>
            🔒 Private — only you<br />
            🕊️ Legacy — shared after death<br />
            📦 Archive — exported to family
          </div>
        </div>
      )}
    </div>
  )
}

function LocationBadge({ location }: { location: string | null }) {
  if (!location) return null
  const loc = LOCATIONS.find(l => l.value === location)
  if (!loc) return null
  return (
    <span style={{
      fontSize: '11px', color: '#1A3A5C',
      background: 'rgba(26,58,92,0.08)',
      padding: '2px 8px', borderRadius: '9999px',
    }}>
      {loc.icon} {loc.label}
    </span>
  )
}

export default function Journal() {
  const [content, setContent] = useState('')
  const [moodScore, setMoodScore] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [entries, setEntries] = useState<Entry[]>([])
  const [loadingEntries, setLoadingEntries] = useState(true)
  const [savedMessage, setSavedMessage] = useState(false)
  const [mainCrisisMessage, setMainCrisisMessage] = useState<string | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<LocationContext>(null)
  const [locationPrompt, setLocationPrompt] = useState<string | null>(null)

  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [replySaving, setReplySaving] = useState(false)
  const [repliesByEntry, setRepliesByEntry] = useState<Record<string, Entry[]>>({})
  const [followUpByEntry, setFollowUpByEntry] = useState<Record<string, string>>({})
  const [crisisByEntry, setCrisisByEntry] = useState<Record<string, CrisisSupport>>({})

  const [recordingState, setRecordingState] = useState<RecordingState>('idle')
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [micError, setMicError] = useState('')
  const [showWalkAndTalk, setShowWalkAndTalk] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    loadEntries()
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  const loadEntries = async () => {
    try {
      const res = await api.get('/entries?limit=20')
      const allEntries: Entry[] = res.data.entries || []
      setEntries(allEntries.filter(e => !e.parent_entry_id))
    } catch (err) {
      console.error('Failed to load entries', err)
    } finally {
      setLoadingEntries(false)
    }
  }

  const loadReplies = async (entryId: string) => {
    try {
      const res = await api.get('/entries/' + entryId + '/replies')
      setRepliesByEntry(prev => ({ ...prev, [entryId]: res.data.replies || [] }))
    } catch (err) {
      console.error('Failed to load replies', err)
    }
  }

  const handleLocationSelect = (location: LocationContext) => {
    setSelectedLocation(location)
    const loc = LOCATIONS.find(l => l.value === location)
    if (loc) setLocationPrompt(loc.prompt)
  }

  const startRecording = async () => {
    setMicError('')
    audioChunksRef.current = []
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop())
        await transcribeRecording()
      }

      mediaRecorder.start(100)
      setRecordingState('recording')
      setRecordingSeconds(0)

      timerRef.current = setInterval(() => {
        setRecordingSeconds(prev => {
          if (prev >= 120) { stopRecording(); return prev }
          return prev + 1
        })
      }, 1000)

    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setMicError('Microphone access denied. Please allow access in your browser settings.')
      } else if (err.name === 'NotFoundError') {
        setMicError('No microphone found. Please connect a microphone and try again.')
      } else {
        setMicError('Could not access microphone. Please try again.')
      }
      setRecordingState('idle')
    }
  }

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
      setRecordingState('transcribing')
    }
  }

  const transcribeRecording = async () => {
    if (audioChunksRef.current.length === 0) { setRecordingState('idle'); return }
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
      const formData = new FormData()
      formData.append('audio', audioBlob, 'recording.webm')

      const token = localStorage.getItem('velumio_token')
      const response = await fetch(
        (process.env.REACT_APP_API_URL || 'http://localhost:3000') + '/entries/voice',
        {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData,
        }
      )

      const data = await response.json()

      if (data.transcription) {
        setContent(prev => prev ? prev + ' ' + data.transcription : data.transcription)
        setRecordingState('done')
        setTimeout(() => setRecordingState('idle'), 2000)
        if (data.crisis_support) setMainCrisisMessage(data.crisis_support.message)
        loadEntries()
      } else {
        setMicError('Could not transcribe audio. Please try speaking more clearly.')
        setRecordingState('idle')
      }
    } catch (err) {
      console.error('Transcription failed:', err)
      setMicError('Transcription failed. Please try again.')
      setRecordingState('idle')
    }
  }

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return mins + ':' + (secs < 10 ? '0' : '') + secs
  }

  const handleSave = async () => {
    if (content.trim().length === 0) return
    setSaving(true)
    try {
      const res = await api.post('/entries', {
        content: content,
        mood_score: moodScore,
        input_method: 'text',
        tags: [],
        privacy_level: 'private',
        location_context: selectedLocation || null,
      })
      setContent('')
      setMoodScore(null)
      setSelectedLocation(null)
      setLocationPrompt(null)
      setSavedMessage(true)
      setTimeout(() => setSavedMessage(false), 3000)

      if (res.data.crisis_support) {
        setMainCrisisMessage(res.data.crisis_support.message)
      } else {
        setMainCrisisMessage(null)
      }

      loadEntries()
    } catch (err) {
      console.error('Failed to save entry', err)
    } finally {
      setSaving(false)
    }
  }

  const handleSendReply = async (entryId: string) => {
    if (replyText.trim().length === 0) return
    setReplySaving(true)
    try {
      const res = await api.post('/entries/' + entryId + '/reply', {
        content: replyText,
        privacy_level: 'private',
      })
      setReplyText('')
      setReplyingTo(null)

      if (res.data.crisis_support) {
        setCrisisByEntry(prev => ({ ...prev, [entryId]: res.data.crisis_support }))
      } else if (res.data.follow_up) {
        setFollowUpByEntry(prev => ({ ...prev, [entryId]: res.data.follow_up }))
      }

      loadReplies(entryId)
    } catch (err) {
      console.error('Failed to send reply', err)
    } finally {
      setReplySaving(false)
    }
  }

  const moodEmojis = ['😞', '😕', '😐', '🙂', '😊']

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short',
    })
  }

  return (
    <div className="animate-fadeUp">

      <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: '4px' }}>
        Your Journal
      </h2>
      <p style={{ color: 'var(--muted)', marginBottom: '32px' }}>
        Write or speak freely. Everything here is encrypted and private to you.
      </p>

      <div style={{
        background: 'var(--white)', borderRadius: 'var(--radius-lg)',
        padding: '28px', boxShadow: 'var(--shadow-md)',
        border: '1px solid var(--cream-dark)', marginBottom: '32px',
      }}>

        {/* Location Selector */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{
            fontSize: '12px', color: 'var(--muted)',
            marginBottom: '10px', fontWeight: 500,
          }}>
            Where are you right now?
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const }}>
            {LOCATIONS.map(loc => (
              <button
                key={String(loc.value)}
                onClick={() => handleLocationSelect(loc.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '7px 14px', fontSize: '13px',
                  borderRadius: '9999px', cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  transition: 'all var(--transition-fast)',
                  border: selectedLocation === loc.value
                    ? '2px solid var(--navy)'
                    : '1.5px solid var(--warm-border)',
                  background: selectedLocation === loc.value
                    ? 'rgba(15,27,45,0.08)'
                    : 'var(--cream)',
                  color: selectedLocation === loc.value
                    ? 'var(--navy)'
                    : 'var(--muted)',
                  fontWeight: selectedLocation === loc.value ? 600 : 400,
                }}
              >
                <span>{loc.icon}</span>
                <span>{loc.label}</span>
              </button>
            ))}
          </div>

          {locationPrompt && (
            <div style={{
              marginTop: '12px', padding: '12px 16px',
              background: 'rgba(15,27,45,0.04)',
              borderRadius: 'var(--radius-md)',
              borderLeft: '3px solid var(--navy)',
              fontSize: '13px', fontStyle: 'italic',
              color: 'var(--ink-soft)', lineHeight: 1.6,
              fontFamily: 'var(--font-display)',
            }}>
              {locationPrompt}
            </div>
          )}
        </div>

        <div style={{
          borderTop: '1px solid var(--cream-dark)',
          marginBottom: '16px',
        }} />

        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder={
            locationPrompt
              ? 'Start writing here...'
              : recordingState === 'transcribing'
              ? 'Transcribing your voice...'
              : recordingState === 'done'
              ? 'Voice entry added — you can edit before saving'
              : "What's on your mind today? Type here or tap the mic to speak."
          }
          style={{
            width: '100%', minHeight: '140px', border: 'none', outline: 'none',
            resize: 'vertical', fontFamily: 'var(--font-body)',
            fontSize: '16px', color: 'var(--ink)', lineHeight: 1.7,
            background: 'transparent',
          }}
        />

        {/* Voice controls */}
        <div style={{
          marginTop: '16px', paddingTop: '16px',
          borderTop: '1px solid var(--cream-dark)',
        }}>
          {recordingState === 'idle' && (
            <div style={{
              display: 'flex', alignItems: 'center',
              gap: '12px', flexWrap: 'wrap' as const,
            }}>
              <button
                onClick={startRecording}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 18px', background: 'var(--cream)',
                  border: '1.5px solid var(--warm-border)',
                  borderRadius: '9999px', cursor: 'pointer',
                  fontSize: '14px', color: 'var(--ink-soft)',
                  fontFamily: 'var(--font-body)',
                  transition: 'all var(--transition-fast)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'var(--gold)'
                  e.currentTarget.style.color = 'var(--gold)'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--warm-border)'
                  e.currentTarget.style.color = 'var(--ink-soft)'
                }}
              >
                <span style={{ fontSize: '18px' }}>🎤</span>
                Tap to speak
              </button>

              <button
                onClick={() => setShowWalkAndTalk(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 18px', background: 'var(--navy)',
                  border: '1.5px solid var(--navy)',
                  borderRadius: '9999px', cursor: 'pointer',
                  fontSize: '14px', color: 'var(--cream)',
                  fontFamily: 'var(--font-body)',
                  transition: 'all var(--transition-fast)',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.85' }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
              >
                <span style={{ fontSize: '18px' }}>🚶</span>
                Walk & Talk
              </button>

              <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                Too tired to type? Just talk.
              </span>
            </div>
          )}

          {recordingState === 'recording' && (
            <div style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '12px', height: '12px', borderRadius: '50%',
                  background: 'var(--rose)',
                  animation: 'pulse 1s ease infinite',
                }} />
                <span style={{ fontSize: '14px', color: 'var(--rose)', fontWeight: 500 }}>
                  Recording — {formatRecordingTime(recordingSeconds)}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  Speak naturally
                </span>
              </div>
              <button
                onClick={stopRecording}
                style={{
                  padding: '10px 18px', background: 'var(--rose)',
                  color: 'white', border: 'none', borderRadius: '9999px',
                  cursor: 'pointer', fontSize: '14px',
                  fontFamily: 'var(--font-body)', fontWeight: 500,
                }}
              >
                Stop recording
              </button>
            </div>
          )}

          {recordingState === 'transcribing' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '16px', height: '16px',
                border: '2px solid var(--warm-border)',
                borderTopColor: 'var(--gold)', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <span style={{ fontSize: '14px', color: 'var(--muted)' }}>
                Transcribing your voice with Whisper AI...
              </span>
            </div>
          )}

          {recordingState === 'done' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'var(--green)', fontSize: '16px' }}>✓</span>
              <span style={{ fontSize: '14px', color: 'var(--green)' }}>
                Voice transcribed — review and save below
              </span>
            </div>
          )}

          {micError && (
            <div style={{
              padding: '10px 14px', background: 'var(--rose-light)',
              borderRadius: 'var(--radius-md)', fontSize: '13px',
              color: 'var(--rose)', marginTop: '8px',
            }}>
              {micError}
            </div>
          )}
        </div>

        {/* Mood + Save */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: '16px', paddingTop: '16px',
          borderTop: '1px solid var(--cream-dark)',
          flexWrap: 'wrap' as const, gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: 'var(--muted)', marginRight: '4px' }}>
              How are you feeling?
            </span>
            {moodEmojis.map((emoji, i) => {
              const value = i * 2 + 2
              const isSelected = moodScore === value
              return (
                <button key={i} onClick={() => setMoodScore(value)} style={{
                  fontSize: '20px',
                  background: isSelected ? 'var(--cream-dark)' : 'transparent',
                  border: 'none', borderRadius: 'var(--radius-md)',
                  padding: '4px 8px', cursor: 'pointer',
                  opacity: isSelected || moodScore === null ? 1 : 0.4,
                }}>
                  {emoji}
                </button>
              )
            })}
          </div>
          <Button
            onClick={handleSave} loading={saving}
            disabled={content.trim().length === 0} size="md"
          >
            Save entry
          </Button>
        </div>

        {mainCrisisMessage ? (
          <div style={{
            marginTop: '16px', background: 'var(--rose-light)',
            borderRadius: 'var(--radius-md)', padding: '16px',
            fontSize: '13px', color: 'var(--rose)',
            lineHeight: 1.6, whiteSpace: 'pre-line' as const,
          }}>
            {mainCrisisMessage}
          </div>
        ) : savedMessage ? (
          <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--green)' }}>
            ✓ Saved — your entry is being gently analysed in the background
          </div>
        ) : null}
      </div>

      {/* Entry history */}
      <h3 style={{
        fontFamily: 'var(--font-display)', fontSize: '18px', marginBottom: '16px',
      }}>
        Recent entries
      </h3>

      {loadingEntries ? (
        <p style={{ color: 'var(--muted)' }}>Loading your entries...</p>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ fontSize: '40px', marginBottom: '16px' }}>✦</p>
          <p style={{
            fontFamily: 'var(--font-display)', fontSize: '18px',
            marginBottom: '8px', color: 'var(--navy)',
          }}>
            No entries yet
          </p>
          <p style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: 1.6 }}>
            Write or speak your first entry above — it only takes a minute.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {entries.map(entry => {
            const replies = repliesByEntry[entry.id] || []
            const crisis = crisisByEntry[entry.id]
            const followUp = followUpByEntry[entry.id]

            return (
              <div key={entry.id} style={{
                background: 'var(--white)', borderRadius: 'var(--radius-lg)',
                padding: '20px', border: '1px solid var(--cream-dark)',
              }}>

                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: '8px',
                }}>
                  <span style={{
                    fontSize: '12px', color: 'var(--muted)',
                    fontFamily: 'var(--font-mono)',
                  }}>
                    {formatDate(entry.entry_date)}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LocationBadge location={entry.location_context} />
                    {entry.input_method === 'voice' && (
                      <span style={{
                        fontSize: '11px', color: 'var(--gold)',
                        background: 'rgba(200,150,62,0.1)',
                        padding: '2px 8px', borderRadius: '9999px',
                      }}>
                        🎤 voice
                      </span>
                    )}
                    <PrivacyBadge
                      entryId={entry.id}
                      privacyLevel={entry.privacy_level || 'private'}
                      onUpdate={(newLevel) => {
                        setEntries(prev => prev.map(e =>
                          e.id === entry.id
                            ? { ...e, privacy_level: newLevel }
                            : e
                        ))
                      }}
                    />
                  </div>
                </div>

                <p style={{
                  fontSize: '14px', color: 'var(--ink-soft)', lineHeight: 1.6,
                  marginBottom: entry.ai_insight && entry.ai_insight.coach_prompt
                    ? '12px' : 0,
                }}>
                  {entry.content.length > 200
                    ? entry.content.slice(0, 200) + '...'
                    : entry.content}
                </p>

                {entry.ai_insight && entry.ai_insight.coach_prompt && (
                  <div style={{
                    borderTop: '1px solid var(--cream-dark)', paddingTop: '12px',
                  }}>
                    <div style={{
                      fontSize: '13px', fontStyle: 'italic', color: 'var(--gold)',
                      fontFamily: 'var(--font-display)', marginBottom: '8px',
                    }}>
                      {entry.ai_insight.coach_prompt}
                    </div>

                    {replies.map(r => (
                      <div key={r.id} style={{
                        background: 'var(--cream)', borderRadius: 'var(--radius-md)',
                        padding: '10px 14px', marginBottom: '8px',
                        fontSize: '13px', color: 'var(--ink-soft)',
                      }}>
                        {r.content}
                      </div>
                    ))}

                    {followUp && (
                      <div style={{
                        fontSize: '13px', fontStyle: 'italic', color: 'var(--gold)',
                        fontFamily: 'var(--font-display)', marginTop: '8px',
                      }}>
                        {followUp}
                      </div>
                    )}

                    {crisis && (
                      <div style={{
                        background: 'var(--rose-light)', borderRadius: 'var(--radius-md)',
                        padding: '14px', marginTop: '8px', fontSize: '13px',
                        color: 'var(--rose)', lineHeight: 1.6,
                        whiteSpace: 'pre-line' as const,
                      }}>
                        {crisis.message}
                      </div>
                    )}

                    {replyingTo === entry.id ? (
                      <div style={{ marginTop: '12px' }}>
                        <textarea
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          placeholder="Reply honestly — no one else sees this"
                          style={{
                            width: '100%', minHeight: '60px',
                            padding: '10px 12px',
                            border: '1.5px solid var(--warm-border)',
                            borderRadius: 'var(--radius-md)',
                            fontFamily: 'var(--font-body)',
                            fontSize: '13px', outline: 'none',
                            resize: 'vertical' as const,
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                          <Button size="sm" loading={replySaving}
                            disabled={replyText.trim().length === 0}
                            onClick={() => handleSendReply(entry.id)}>
                            Send reply
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => { setReplyingTo(null); setReplyText('') }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setReplyingTo(entry.id)
                          loadReplies(entry.id)
                        }}
                        style={{
                          marginTop: '4px', fontSize: '12px',
                          color: 'var(--muted)', background: 'none',
                          border: 'none', cursor: 'pointer',
                          padding: 0, textDecoration: 'underline',
                        }}
                      >
                        Reply to this
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {showWalkAndTalk && (
        <WalkAndTalk
          onClose={() => setShowWalkAndTalk(false)}
          onTranscribed={(text) => {
            setContent(prev => prev ? prev + ' ' + text : text)
            setShowWalkAndTalk(false)
            loadEntries()
          }}
        />
      )}

    </div>
  )
}